import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { CheckCircle2, GitMerge } from 'lucide-react';
import type { Contact } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { Avatar, Button, EmptyState, ErrorState, Modal, SkeletonLines, useToast } from '../ui';

function groupKey(g: Contact[]): string {
  return g
    .map((c) => c.id)
    .sort()
    .join('|');
}

export function DuplicateFinder({ open, onClose }: { open: boolean; onClose: () => void }) {
  const q = useQuery({ queryKey: qk.contactDuplicates, queryFn: () => api('GET /api/contacts/duplicates'), enabled: open });
  const groups = q.data?.groups ?? [];
  return (
    <Modal open={open} onClose={onClose} size="lg" title="Find duplicates" description="Contacts that share an email address or name. Pick the record to keep — the others are merged into it.">
      {q.isLoading ? (
        <SkeletonLines lines={6} />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : groups.length === 0 ? (
        <EmptyState icon={<CheckCircle2 className="h-6 w-6" />} title="No duplicates found" description="Your contacts look tidy." />
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <DuplicateGroup key={groupKey(g)} group={g} />
          ))}
        </div>
      )}
    </Modal>
  );
}

function DuplicateGroup({ group }: { group: Contact[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const initial = [...group].sort((a, b) => b.interactionCount - a.interactionCount)[0]?.id ?? '';
  const [primaryId, setPrimaryId] = useState(initial);
  const name = `dup-${groupKey(group)}`;

  const merge = useMutation({
    mutationFn: () => api('POST /api/contacts/merge', { body: { primaryId, mergeIds: group.filter((c) => c.id !== primaryId).map((c) => c.id) } }),
    onSuccess: (r) => {
      toast.success(`Merged into ${r.contact.name || r.contact.emails[0] || 'primary contact'}`);
      void qc.invalidateQueries({ queryKey: qk.contactDuplicates });
      void qc.invalidateQueries({ queryKey: qk.contactsRoot });
      void qc.invalidateQueries({ queryKey: qk.contact(r.contact.id) });
    },
    onError: (e) => toast.error(e),
  });

  return (
    <fieldset className="rounded-2xl border border-hairline bg-raised/40 p-3">
      <legend className="px-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">{group.length} possible duplicates — choose the primary record</legend>
      <div className="space-y-2">
        {group.map((c) => (
          <label
            key={c.id}
            className={clsx(
              'flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 transition-colors',
              c.id === primaryId ? 'border-primary/50 bg-primary-soft' : 'border-hairline bg-surface hover:bg-raised',
            )}
          >
            <input type="radio" name={name} checked={c.id === primaryId} onChange={() => setPrimaryId(c.id)} className="h-4 w-4 accent-[rgb(var(--primary))]" />
            <Avatar name={c.name} email={c.emails[0] ?? c.id} size={32} />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-ink">{c.name || c.emails[0] || 'Unnamed contact'}</span>
              <span className="block truncate text-xs text-muted">
                {c.emails.join(', ') || 'No email'}
                {c.company ? ` · ${c.company}` : ''} · {c.interactionCount} interactions
              </span>
            </span>
          </label>
        ))}
      </div>
      <div className="mt-3 flex justify-end">
        <Button variant="primary" size="sm" icon={<GitMerge className="h-4 w-4" />} loading={merge.isPending} disabled={!primaryId} onClick={() => merge.mutate()} className="max-md:h-11">
          Merge {group.length - 1} into primary
        </Button>
      </div>
    </fieldset>
  );
}
